import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Calendar, Users, Clock, CheckCircle, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'
import api from '@/lib/api'

interface EventStats {
  total_events: number
  upcoming_events: number
  completed_events: number
  total_assignments: number
  pending_assignments: number
  recent_events: Array<{
    id: number
    title: string
    scheduled_start: string
    status: string
    assignment_count: number
  }>
  period_stats: {
    today: { count: number; label: string }
    this_week: { count: number; label: string }
    this_month: { count: number; label: string }
  }
  status_breakdown: Array<{
    status: string
    count: number
    percentage: number
  }>
  assignment_breakdown: Array<{
    status: string
    count: number
    percentage: number
  }>
}

const EventsDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const serviceInstanceId = searchParams.get('service_instance_id')
  const [period, setPeriod] = useState<'today' | 'this_week' | 'this_month'>('this_week')

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['events-dashboard', serviceInstanceId, period],
    queryFn: async () => {
      const response = await api.get<EventStats>('/api/Event/Dashboard/Statistics/', {
        params: {
          service_instance_id: serviceInstanceId,
          period_type: period,
        },
      })
      return response.data
    },
    enabled: !!serviceInstanceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const stats_cards = useMemo(() => {
    if (!stats) return []
    return [
      {
        label: 'Total Events',
        value: stats.total_events,
        icon: Calendar,
        color: 'bg-blue-100 text-blue-600',
        description: 'All events created',
      },
      {
        label: 'Upcoming Events',
        value: stats.upcoming_events,
        icon: Clock,
        color: 'bg-amber-100 text-amber-600',
        description: 'Scheduled for future',
      },
      {
        label: 'Completed Events',
        value: stats.completed_events,
        icon: CheckCircle,
        color: 'bg-green-100 text-green-600',
        description: 'Finished events',
      },
      {
        label: 'Total Assignments',
        value: stats.total_assignments,
        icon: Users,
        color: 'bg-purple-100 text-purple-600',
        description: `${stats.pending_assignments} pending`,
      },
    ]
  }, [stats])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading events dashboard...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-destructive">Failed to load events dashboard</div>
      </div>
    )
  }

  if (!stats) {
    return null
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Events Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of all events and assignments</p>
        </div>
        <Button onClick={() => navigate('/events/create')} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Event
        </Button>
      </div>

      {/* Period Selector */}
      <Tabs value={period} onValueChange={(value) => setPeriod(value as any)} className="w-full max-w-sm">
        <TabsList>
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="this_week">This Week</TabsTrigger>
          <TabsTrigger value="this_month">This Month</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats_cards.map((card) => {
          const Icon = card.icon
          return (
            <Card key={card.label} className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">{card.label}</p>
                  <p className="text-3xl font-bold mt-2">{card.value}</p>
                  <p className="text-xs text-muted-foreground mt-2">{card.description}</p>
                </div>
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Breakdown */}
        <Card className="p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold mb-4">Event Status Breakdown</h2>
          <div className="space-y-4">
            {stats.status_breakdown.map((item) => (
              <div key={item.status} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium capitalize">{item.status}</span>
                    <span className="text-sm font-semibold">{item.count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Assignment Status */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Assignment Status</h2>
          <div className="space-y-3">
            {stats.assignment_breakdown.map((item) => (
              <div key={item.status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium capitalize">{item.status}</span>
                <Badge variant={item.status === 'pending' ? 'destructive' : 'default'}>
                  {item.count}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent Events */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Events</h2>
        {stats.recent_events.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                    Event Name
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                    Date
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                    Assignments
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_events.map((event) => (
                  <tr key={event.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium">{event.title}</td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {new Date(event.scheduled_start).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <Badge variant={event.status === 'completed' ? 'default' : 'secondary'}>
                        {event.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm font-medium">{event.assignment_count}</td>
                    <td className="py-3 px-4 text-sm">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <p>No recent events</p>
          </div>
        )}
      </Card>
    </div>
  )
}

export default EventsDashboard
